import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { AzureSqlDWTableDataset, Dataset, formatConst, LinkedService, Metamodel, Owned, storeConst } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { SynapseAnalyticsSettings } from 'app/model/dataflow/dataset-settings/synapse-analytics-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class SynapseAnalyticsDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('SynapseAnalyticsSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as SynapseAnalyticsSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as SynapseAnalyticsSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.schemaName.expression = !incomingSourceDatasetsSettings.schemaName ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
        outputSourceDatasetsSettings.tableName.expression = !incomingSourceDatasetsSettings.tableName ? undefined : incomingSourceDatasetsSettings.tableName.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        if (selectedRows.length) {
            return [false, ''];
        }
        return [true, ''];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        const rows = selectedRows as IMapperEntity[];
        rows.forEach((elem) => {
            const srcName = elem.entityName.replace(/\[|\]/g, '');
            const tableName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            tableName.expression = srcName.includes('.') ? srcName.split('.')[1] : srcName;
            const schemaName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            schemaName.expression = srcName.includes('.') ? srcName.split('.')[0] : srcName;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dslProps = new Map<string,any>([
                [ DslConnectorConstants.schemaName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(schemaName.expression)],
                [ DslConnectorConstants.tableName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(tableName.expression)]
            ]);

            tables.push({
                // need a valid mapping dataflow source name to pass dataflow validations
                name: getValidTransformationName(true, srcName),
                sourceDataset: elem.dataset,
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                // will initialize datasetSettings post processing Target SynapseAnalytics entity.
                datasetSettings: undefined,
                wildcardPaths: [],
                dslConnectorProperties: dslProps,
                datasetType: DatasetFormatType.SynapseAnalytics,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: srcName
            });
        });

        return tables;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);

        // Create and populate SynapseAnalyticsSettings
        const synapseAnalyticsSettings = Owned.create<SynapseAnalyticsSettings>(SynapseAnalyticsSettings.MODEL, table as DataFlowSource);
        synapseAnalyticsSettings.initialize(undefined);

        synapseAnalyticsSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
        synapseAnalyticsSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;

        return synapseAnalyticsSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        const dataset = new AzureSqlDWTableDataset('SynapseAnalytics');
        dataset.linkedService = linkedService;
        dataset.table = tableName;
        dataset.schema = schemaName;
        return dataset;
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);

        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, formatConst.SQL);
        commonDslConnectorProperties.set(DslConnectorConstants.store, storeConst.SynapseAnalytics);
        commonDslConnectorProperties.set(DslConnectorConstants.deletable, false);
        commonDslConnectorProperties.set(DslConnectorConstants.insertable, true);
        commonDslConnectorProperties.set(DslConnectorConstants.updateable, false);
        commonDslConnectorProperties.set(DslConnectorConstants.upsertable, false);
        commonDslConnectorProperties.set('allowCopyCommand', false);
        commonDslConnectorProperties.set('staged', true);
        commonDslConnectorProperties.set('skipDuplicateMapInputs', true);
        commonDslConnectorProperties.set('skipDuplicateMapOutputs', true);
    }
}
